/**
 * Structured production logger
 * 
 * - DEV: forwards to console (no change in DX)
 * - PROD: routes through Sentry (breadcrumbs + captureException)
 * - No console.* leaks in production builds
 * 
 * Usage:
 *   import { log } from '@/lib/prodLogger';
 *   log.error('[MyHook] fetch failed', error, { userId });
 *   log.info('[SSO] config loaded', { orgId });
 *   log.warn('[Queue] retrying', { attempt: 3 });
 *   log.debug('[Cache] hit'); // DEV-only, no-op in prod
 */

import { Sentry, captureError } from '@/lib/sentry';

const isDev = import.meta.env.DEV;

function addBreadcrumb(
  category: string,
  message: string,
  level: 'info' | 'warning' | 'error' | 'debug',
  data?: Record<string, unknown>
) {
  if (!isDev) {
    Sentry.addBreadcrumb({ category, message, level, data });
  }
}

export const log = {
  /**
   * Debug – DEV console only, no-op in production
   */
  debug(message: string, data?: Record<string, unknown>): void {
    if (isDev) {
      console.debug(message, data ?? '');
    }
  },

  /**
   * Info – console in DEV, Sentry breadcrumb in PROD
   */
  info(message: string, data?: Record<string, unknown>): void {
    if (isDev) {
      console.info(message, data ?? '');
    } else {
      addBreadcrumb('app', message, 'info', data);
    }
  },

  /**
   * Warn – console in DEV, Sentry breadcrumb in PROD
   */
  warn(message: string, data?: Record<string, unknown>): void {
    if (isDev) {
      console.warn(message, data ?? '');
    } else {
      addBreadcrumb('app', message, 'warning', data);
    }
  },

  /**
   * Error – console in DEV, Sentry captureException in PROD
   * Accepts optional Error object as second param
   */
  error(message: string, error?: unknown, context?: Record<string, unknown>): void {
    if (isDev) {
      console.error(message, error ?? '', context ?? '');
    } else {
      if (error instanceof Error) {
        captureError(error, { message, ...context });
      } else if (error) {
        captureError(new Error(message), { originalError: String(error), ...context });
      } else {
        captureError(new Error(message), context);
      }
    }
  },
};
